import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom'

import LoadingSpinner from '@/components/LoadingSpinner'
import useAppInit from '@/hooks/appInit'
import i18n from '@/i18n/i18n'
import AppLayout from '@/layout/AppLayout'
import SidebarLayout from '@/layout/SidebarLayout'
import Error from '@/pages/Error'
import SomethingWentWrong from '@/pages/SomethingWentWrong'
import Profile from '@/pages/settings/Profile'
import Plans from '@/pages/settings/plans/Plans'
import { routes } from '@/routes/routeConfig'

import BasicInfo from './components/onBoarding/BasicInfo'
import Redirect from './components/onBoarding/Redirect'
import StripeWaiting from './components/onBoarding/StripeWaiting'
import TryNow from './components/onBoarding/TryNow'
import VerifyEmail from './components/onBoarding/VerifyEmail'
import RecipeOutPut from './pages/getRecipe/allInOne/RecipeOutPut'
import DentinEnamelOutput from './pages/getRecipe/matisseLite/dentin&enamel/output/DentinEnamelOutput'
import ShadeGuideAiOutput from './pages/getRecipe/shadeGuide/recipeOutput/ShadeGuideAiOutput'
import ProjectDetail from './pages/projectDetail'
import ProjectIndex from './pages/projectDetail/ProjectIndex'
import Trash from './pages/projects/trash/Trash'
import { setActivePlan } from './store/appSlice'
import { getProfileApi } from './utils/appApi'
import { HeaderKey, setHeader } from './utils/httpService'

const AppRoutes = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [isProfileLoading, setIsProfileLoading] = useState(true)
  const [isProfileLoaded, setIsProfileLoaded] = useState(false)
  const [isMultiSession, setIsMultiSession] = useState(false)
  const [signupState, setSignupState] = useState<null | string>(null)
  const [subscriptions, setSubscriptions] = useState<any[]>([])

  const [errorCode, setErrorCode] = useState<string | null>(null)

  const { token, isEmailVerified } = useAppInit()

  useEffect(() => {
    if (token && isEmailVerified) {
      getProfileApi()
        .then((res) => {
          setIsProfileLoading(false)

          if (res?.signup_state) {
            setSignupState(res.signup_state)
            setSubscriptions(res.subscriptions)
          }

          if (res?.subscriptions?.length > 0) {
            dispatch(setActivePlan(res.subscriptions[0]))
          } else {
            if (res?.signup_state === 'sign_up') {
              navigate('/get-started/profile')
              return
            }
            if (res?.signup_state === 'registered') {
              navigate('/get-started/plans')
              return
            }
          }

          if (res?.language) {
            i18n.changeLanguage(res.language)
            localStorage.setItem('i18nCode', res.language)
            setHeader(HeaderKey.LANGUAGE, res.language)
          }
          setIsProfileLoaded(true)
        })
        .catch((err) => {
          setIsProfileLoaded(false)

          if (err?.response?.data?.code === '3005') {
            setIsMultiSession(true)
            setIsProfileLoading(false)
            return
          }

          if (String(err?.response?.data?.code) === '3013') {
            setErrorCode('3013')
            setIsProfileLoading(false)
            return
          }

          if (err && err?.response?.data?.code === '1001') {
            navigate('/get-started/profile')
          }
          setIsProfileLoading(false)
        })
    } else if (token && !isEmailVerified) {
      setIsProfileLoading(false)
    }
  }, [token, isEmailVerified])

  const getDefaultRedirectPath = () => {
    if (isMultiSession) {
      return '/error?code=3005'
    }
    if (errorCode === '3013') {
      return '/error?code=3013'
    }
    if (signupState === 'registered' && subscriptions.length === 0) {
      return '/get-started/plans'
    }
    if (isProfileLoaded) {
      return '/projects'
    }
    return '/get-started/profile'
  }

  if (isProfileLoading) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <LoadingSpinner />
      </div>
    )
  }
  return (
    <Routes>
      <Route path="try" element={<TryNow />} />
      <Route path="/verify-email" element={<VerifyEmail />} caseSensitive />
      <Route path="/stripe-waiting" element={<StripeWaiting />} />
      <Route path="/redirect-to-app" element={<Redirect />} />
      <Route path="/get-started/:step" element={<BasicInfo />} />
      <Route path="/error" element={<Error />} caseSensitive />
      <Route path="/something-went-wrong" element={<SomethingWentWrong />} />
      {!isEmailVerified && (
        <Route path="*" element={<Navigate to="/verify-email" replace />} />
      )}

      <Route element={<AppLayout />}>
        <Route element={<SidebarLayout />}>
          {routes.map((route) => {
            const Element = route.element
            return (
              <Route
                key={route.path}
                path={route.path}
                element={<Element />}
                caseSensitive
              />
            )
          })}
        </Route>
        <Route path="/projects" element={<ProjectIndex />} caseSensitive />
        <Route path="/profile" element={<Profile />} caseSensitive />
        <Route path="/plans" element={<Plans />} caseSensitive />
        <Route
          path="/all-in-one-output"
          element={<RecipeOutPut />}
          caseSensitive
        />

        <Route
          path="/shade-guide-ai-output"
          // element={<ShadeGuideOutput />}
          element={<ShadeGuideAiOutput />}
          caseSensitive
        />
        <Route
          path="/matisse-lite-output"
          element={<DentinEnamelOutput />}
          caseSensitive
        />
        <Route
          path="*"
          element={<Navigate to={getDefaultRedirectPath()} replace />}
        />

        <Route path="projects/trash" element={<Trash />} caseSensitive />
        <Route path="/projects/:id" element={<ProjectDetail />} />
      </Route>
    </Routes>
  )
}

export default AppRoutes
